import React from 'react';
import _ from 'lodash';
import axios from 'axios';
import R from 'ramda';
import moment from 'moment';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Message,
  Grid,
  Segment,
  Icon,
  Input,
  Dropdown,
  Menu,
  Divider,
  Label,
  Button,
  Popup,
} from 'semantic-ui-react';
import { DyzioPrimaryButton } from 'dyzio-shared-components/lib/components/dyzio-buttons';
import { Link } from 'react-router';
import { compose } from 'recompose';

import CampaignAuditTrail from '../campaign-audit-trail';
import StatusBar from '../status-bar';

import HashtagScannerModal from '../../components/hashtag-scanner-modal';
import AddCampaignModal from '../../components/add-campaign-modal';
import Report from '../../components/report';

import { loadingCampaign } from './actions';
import { loadCampaignAnalytics } from '../campaign-analytics/actions';

import { default as DyzioDropdown } from '../../components/dropdown';

import ACRSetup from '../../components/acr-setup';

import { clearStatusBar } from '../../containers/status-bar/actions';
import { authService } from '../../utils/auth-service';

import hasRESTClient from '../../hocs/has-rest-client';
import hasOrgansation from '../../hocs/has-organisation';

import { isTeam, KASPERSKY, TD } from './../../utils/private-data-whitelist';


import {
  CAMPAIGN_OWNER,
  CAMPAIGN_ADMIN,
  TEAM_MEMBER,
  PARTNER_AGENCY,
  CLIENT,
  INFLUENCER,
  TALENT_AGENT,
  PRODUCTION_COMPANY,
  ANALYTICS,
  AUDIT_TRAIL,
  COLLABORATIONS,
  NOTIFICATIONS,
  PAYMENTS,
  SEND_BRIEF_ALL,
  SEND_DELIVERABLE_ALL,
  GROUP_CHATS,
  FACEBOOK,
  TWITTER,
  INSTAGRAM,
} from '../../utils/static-configuration';
import DiscussionsList from '../../containers/discussion-list';
import { checkUserIsTeamMember, checkUserHasAdminRights } from '../../utils/role-service';
import { updateCampaign } from '../../utils/jsonapi-functions/campaigns';
import { reportError } from '../../utils/error-handling-service';
import CampaignBudget from '../../components/campaign-budget';
import CampaignNotificationsTab from '../../components/campaign-notifications-tab';
import Payments from '../../containers/payments';
import { setModal } from '../../actions/modal';
import SendBriefAllModal from '../../containers/send-brief-all-modal';
import SendDeliverableAllModal from '../../containers/send-deliverable-all-modal';
import ThemeSelector from '../../themes/theme-selector';

const DEBOUNCE_TIMEOUT = 800;

const currentTheme = ThemeSelector.theme();

const cardStyles = {
  width: '98%',
  margin: '20px auto',
};

const INITIAL_HASHTAG_SCANNER_STATE = {
  hashtagScannerModalOpen: false,
  isScanning: false,
  validHashtags: true,
  hashtagsErrors: [],
  currentScannedPlatform: null,
  scannerResultsCount: null,
  hashtags: [],
};

const APPROVED_OPTIONS = {
  color: 'pink',
  text: 'Your ACR is approved and will collect data now',
  icon: 'check',
};

const PENDING_OPTIONS = {
  color: 'orange',
  text: 'Your ACR is awaiting approval, please use the chat tool (bottom right) to contact us if necessary',
  icon: 'clock outline',
};

const ApprovalStatusLabel = ({ approvalStatus }) => {
  const options = approvalStatus === 'APPROVED' ? APPROVED_OPTIONS : PENDING_OPTIONS;
  return (
    <Popup
      trigger={
        <Label
          size="large"
          color={options.color}
          basic
        >
          <Icon name={options.icon} />{approvalStatus}
        </Label>
      }
      content={options.text}
    />
  );
};

class Campaign extends React.Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  static propTypes = {
    routeParams: PropTypes.object.isRequired,
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      campaignId: null,
      budgetAllocated: 'Waiting...',
      activeTab: COLLABORATIONS,
      filterDiscussionListByTerm: null,
      editCampaignModalOpen: false,
      campaignUpdatedFields: {},
      ...INITIAL_HASHTAG_SCANNER_STATE,
    };

    this.updateCampaign = this.updateCampaign.bind(this);

    this.hashtagScannerAPI = axios.create({
      baseURL: 'https://b7o8doqzfb.execute-api.eu-west-1.amazonaws.com/production/hashtags',
      timeout: 20000,
      headers: {
        Authorization: `Bearer ${authService.getToken()}`,
      },
    });
    this.automationrAPI = axios.create({
      baseURL: process.env.REACT_APP_AUTOMATION_API,
      timeout: 20000,
      headers: {
        Authorization: `Bearer ${authService.getToken()}`,
      },
    });
  }

  componentDidMount() {
    const { campaignAPIClient, userInfo: { organisationId } } = this.props;
    const myUserRole = this.props.myUserRole;
    if (myUserRole === CLIENT || myUserRole === PARTNER_AGENCY) {
      this.context.router.push('/dashboard');
    }
    const campaignId = this.props.routeParams ? this.props.routeParams.id : '';

    const query = this.props.location.query;
    if (query.hasOwnProperty('collaborations')) {
      this.setState({ campaignId, activeTab: COLLABORATIONS });
    } else if (query.hasOwnProperty('groupchats')) {
      this.setState({ campaignId, activeTab: GROUP_CHATS });
    } else if (query.hasOwnProperty('analytics')) {
      this.setState({ campaignId, activeTab: ANALYTICS });
    } else if (query.hasOwnProperty('payments')) {
      this.setState({ campaignId, activeTab: PAYMENTS });
    } else if (query.hasOwnProperty('notifications')) {
      this.setState({ campaignId, activeTab: NOTIFICATIONS });
    } else if (query.hasOwnProperty('audit-trail')) {
      this.setState({ campaignId, activeTab: AUDIT_TRAIL });
    } else {
      this.setState({ campaignId, activeTab: COLLABORATIONS });
      this.context.router.push(`${this.props.location.pathname}?collaborations`);
    }

    this.props.onLoadingCampaign(campaignId);
    this.setState({
      isLoadingTotals: true,
    });
    const requests = [
      campaignAPIClient.get(`/summary/tracked-assets?organisationId=${organisationId}`),
    ];
    return Promise.all(requests).then(([{ data: { data: { trackedAssetsCount } } }]) => this.setState({
      trackedAssetsCount,
      isLoadingTotals: false,
    }));
  }

  componentDidUpdate() {
    const newCampaignId = this.props.routeParams ? this.props.routeParams.id : '';
    if (this.state.campaignId !== newCampaignId) {
      this.setState({ campaignId: newCampaignId, activeTab: COLLABORATIONS });
      this.context.router.push(`/acr-campaign/${newCampaignId}?collaborations`);
      // this.props.onClearDiscussions();
      this.props.onLoadingCampaign(newCampaignId);
    }
  }

  componentWillUnmount() {
    // this.props.onClearStatusBar();
    // this.props.onClearDiscussions();
  }

  async updateCampaign(budget) {
    const id = this.props.campaign.get('id');
    const oldBudget = this.props.campaign.get('budget');

    try {
      updateCampaign(id, budget);
    } catch (error) {
      reportError('UPDATE_CAMPAIGN_ERROR', error.message);
      this.props.onSetStatusBar(error.message, 'red');
    }
  }

  setActiveTab = activeTab => () => {
    if (activeTab === COLLABORATIONS && !this.props.location.query.hasOwnProperty('collaborations')) {
      this.context.router.push(`${this.props.location.pathname}?collaborations`);
    }
    if (activeTab === GROUP_CHATS && !this.props.location.query.hasOwnProperty('groupchats')) {
      this.context.router.push(`${this.props.location.pathname}?groupchats`);
    }
    if (activeTab === ANALYTICS && !this.props.location.query.hasOwnProperty('analytics')) {
      this.context.router.push(`${this.props.location.pathname}?analytics`);
    }
    if (activeTab === PAYMENTS && !this.props.location.query.hasOwnProperty('payments')) {
      this.context.router.push(`${this.props.location.pathname}?payments`);
    }
    if (activeTab === AUDIT_TRAIL && !this.props.location.query.hasOwnProperty('audit-trail')) {
      this.context.router.push(`${this.props.location.pathname}?audit-trail`);
    }
    if (activeTab === NOTIFICATIONS && !this.props.location.query.hasOwnProperty('notifications')) {
      this.context.router.push(`${this.props.location.pathname}?notifications`);
    }
    this.setState({ activeTab });
  }

  setModal = modalName => this.props.onSetModal(modalName);

  feedback = action => this.context.router.push(action)

  openReport = type => {
    const { campaignId } = this.state;
    window.open(`${currentTheme.shareSite}/report/${campaignId}/${type}`);
  }

  handleHashtagInputsChange = (e, { name, value }) => {
    const { hashtags: stateHashtags } = this.state;
    const hashtags = _.clone(stateHashtags);
    const existing = _.find(hashtags, ['name', name]);
    if (existing) {
      existing.value = value;
    } else {
      hashtags.push({
        name,
        value,
      });
    }

    this.setState({
      hashtags,
    });
  }

  handleHashtagScannerState = () => {
    const { hashtagScannerModalOpen } = this.state;
    this.setState({
      hashtagScannerModalOpen: !hashtagScannerModalOpen,
    });
  }

  scan = function* scan() {
    const { campaign } = this.props;
    const { hashtags } = this.state;
    const campaignId = campaign.get('id');
    const query = platform => this.hashtagScannerAPI.post('/scan', {
      data: {
        campaignId,
        platform: _.lowerCase(platform),
        hashtags: _.map(hashtags, hashtag => _.trim(hashtag.value)),
      },
    });
    // yield query(YOUTUBE);
    yield query(INSTAGRAM);
    yield query(TWITTER);
    yield query(FACEBOOK);
  }

  getHashtagsErrors = () => {
    const REGEX = /^(#|@?)\S{3,}$/g; // allow wide chars in hashtags
    const { hashtags } = this.state;
    const values = _.map(hashtags, (hashtag => _.trim(hashtag.value)));
    const matches = _.map(values, (value => value.match(REGEX)));
    const errors = _.map(matches, (value, index) => ({ isError: _.isNull(value), position: index }));
    return errors;
  }

  scanHashtags = () => {
    const { campaign } = this.props;
    const TIMEOUT = 2200;
    const generator = this.scan();
    const channels = [INSTAGRAM, TWITTER, FACEBOOK];
    _.forEach(channels, async (channel, index) => {
      const isFacebook = channel === FACEBOOK;
      setTimeout(async () => {
        const { data: { data } } = await generator.next().value;
        const state = {
          currentScannedPlatform: channel,
          scannerResultsCount: _.sumBy(data, item => item.trackedAssets.length || 0),
        };
        this.setState({
          ...state,
        }, () => {
          if (isFacebook) {
            _.delay(() => this.setState({
              ...INITIAL_HASHTAG_SCANNER_STATE,
            }, () => this.props.onLoadingCampaign(campaign.get('id'))), TIMEOUT);
          }
        });
      }, TIMEOUT * R.inc(index));
    });
  }

  toggleEditCampaignModalState = () => {
    this.setState(prevState => ({
      editCampaignModalOpen: !prevState.editCampaignModalOpen,
    }));
  }

  handleScanButtonClick = () => {
    const hashtagsErrors = this.getHashtagsErrors();
    const hasError = _.find(hashtagsErrors, ['isError', true]);
    if (!hasError) {
      this.setState({
        isScanning: true,
        hashtagsErrors: [],
      }, this.scanHashtags);
    } else {
      this.setState({
        hashtagsErrors,
      });
    }
  }

  onHashtagScannerModalClose = () => {
    this.setState({
      ...INITIAL_HASHTAG_SCANNER_STATE,
    });
  }

  onFilterDiscussionListByTermChanged = _.debounce((event, data) => {
    const { value } = data;
    this.setState({
      filterDiscussionListByTerm: value,
    });
  }, DEBOUNCE_TIMEOUT);

  onEditCampaignTextField = (event, { name, value }) => {
    const { campaignUpdatedFields } = this.state;
    _.set(campaignUpdatedFields, name, value);
    this.setState({
      campaignUpdatedFields,
    });
  }

  onLiveDateChange = date => {
    const value = date.format();
    this.onEditCampaignTextField(null, { name: 'liveDate', value });
  }

  onEndDateChange = date => {
    const value = date.format();
    this.onEditCampaignTextField(null, { name: 'endDate', value });
  }

  onCampaignCurrencyChanged = (event, { value }) => {
    this.onEditCampaignTextField(null, { name: 'currency', value });
  }

  onSubmitEditCampaignForm = () => {
    const { campaignUpdatedFields } = this.state;
    const { name, budget, id, currency, endDate, liveDate, hashtags } = campaignUpdatedFields;
    const { restClient } = this.props;
    const attributes = {
      name,
      budget,
      currency,
      liveDate,
      endDate,
      hashtags,
    };
    restClient.patch(`campaigns/${id}`, {
      data: {
        attributes: _.pickBy(attributes),
      },
    }).then(() => {
      this.setState({
        editCampaignModalOpen: false,
      }, () => this.props.onLoadingCampaign(id));
    });
  }

  onSettingsDropdownItemClick = value => {
    if (value === 'campaign-settings') {
      const { campaign } = this.props;
      const campaignJSON = campaign.toJSON();
      const { name, budget, currency, liveDate, endDate, hashtags } = campaignJSON;
      this.setState({
        editCampaignModalOpen: true,
        campaignUpdatedFields: {
          name, budget, currency, liveDate, endDate, hashtags, id: campaign.get('id'),
        },
      });
    } else {
      this.context.router.push(value);
    }
  }

  handleACRScan = () => {
    const { campaignId } = this.state;
    this.setState({
      automationScanLoading: true,
    });
    this.automationrAPI.post(`/run-acr-scan/${campaignId}`)
      .then(() => this.setState({
        automationScanLoading: false,
      }))
      .then(() => this.setState({ campaignId, activeTab: ANALYTICS }))
      .catch(e => reportError('ACR_ERROR', e));
  }

  handleAutomatedCampaignScan = () => {
    const { campaignId } = this.state;
    this.setState({
      automationScanLoading: true,
    });
    this.automationrAPI.post(`/run-automated-scan/${campaignId}`)
      .then(() => this.setState({
        automationScanLoading: false,
      }))
      .then(() => this.setState({ campaignId, activeTab: ANALYTICS }))
      .catch(e => reportError('AUTO_CAMPAIGN_ERROR', e));
  }

  render() {
    const {
      myUserRole,
      campaign,
      campaignLoading,
      campaignLoadError,
      members,
      showModal,
      userInfo,
      organisation,
    } = this.props;

    const { organisationId } = userInfo;

    const { isUserSuperAdmin } = userInfo;

    const isDyzioTeam = isTeam(organisationId, TD);
    const isKaspersky = isTeam(organisationId, KASPERSKY);
    const shouldRenderAuditTrail = isDyzioTeam || (isKaspersky && isUserSuperAdmin);

    const {
      activeTab,
      campaignId,
      hashtagScannerModalOpen,
      isScanning,
      currentScannedPlatform,
      scannerResultsCount,
      hashtags,
      hashtagsErrors,
      campaignUpdatedFields,
      filterDiscussionListByTerm,
      editCampaignModalOpen,
      trackedAssetsCount,
      automationScanLoading,
    } = this.state;

    if (!campaignId) return null;

    const campaignName = campaign && campaign.get('name');
    const budget = campaign.get('budget') || 0;
    const currency = campaignUpdatedFields && campaignUpdatedFields.currency;
    const liveDate = campaignUpdatedFields && moment(campaignUpdatedFields.liveDate);
    const endDate = campaignUpdatedFields && moment(campaignUpdatedFields.endDate);

    const campaignCurrency = campaign.get('currency');
    const campaignJSON = campaign.toJSON();
    const { campaignType, approvalStatus } = campaignJSON;

    const campaignFormProps = {
      name: campaignUpdatedFields.name,
      budget: campaignUpdatedFields.budget,
      currency,
      liveDate,
      endDate,
      campaignType,
      hashtags: campaignUpdatedFields.hashtags,
      onInputChange: this.onEditCampaignTextField,
      onLiveDateChange: this.onLiveDateChange,
      onEndDateChange: this.onEndDateChange,
      onCurrencyChange: this.onCampaignCurrencyChanged,
    };

    const teamMembers = [];
    const clientMembers = [];
    const creatorMembers = [];

    if (members) {
      members.forEach(member => {
        const role = member.get('role');
        if (role === TEAM_MEMBER || role === CAMPAIGN_OWNER || role === CAMPAIGN_ADMIN) teamMembers.push(member);
        if (role === CLIENT) clientMembers.push(member);
        if (role === PRODUCTION_COMPANY || role === INFLUENCER || role === TALENT_AGENT) creatorMembers.push(member);
      });
    }


    const noDiscussions = _.isEmpty(campaignJSON.discussions);

    const isManual = campaignType === 'WORKFLOW';
    const isACR = campaignType === 'ACR';
    const isAutomatedCampaign = campaignType === 'AUTOMATED';
    const isAuto = (isACR || isAutomatedCampaign);
    const hasEvents = isACR || isAutomatedCampaign;
    const hasInfluencers = creatorMembers.length > 0 || campaign && (campaignJSON.pendingInvitations || []).length > 0;
    const isUserTeamMember = isUserSuperAdmin || checkUserIsTeamMember(myUserRole);
    const isUserAnAdmin = isUserSuperAdmin || checkUserHasAdminRights(myUserRole);
    const allocatedBudget = _.reduce(campaignJSON.discussions, (sum, discussion) => sum + (discussion.budget || 0), 0);

    const hashtagsArray = _.split(campaignJSON.hashtags, ',');

    const menuOptions = [
      {
        key: 'invite',
        text: 'Invite people to this campaign',
        onClick: () => this.onSettingsDropdownItemClick(`/acr-campaign/${campaignId}/invite`),
      },
    ];

    if (isUserAnAdmin) {
      menuOptions.push({
        key: 'manage',
        text: 'Manage people on this campaign',
        onClick: () => this.onSettingsDropdownItemClick(`/acr-campaign/${campaignId}/manage`),
      });
    }

    menuOptions.push({
      key: 'campaign-settings',
      text: 'Edit campaign details',
      value: 'campaign-settings',
      onClick: () => this.onSettingsDropdownItemClick('campaign-settings'),
    });

    return (
      <div className="page">
        { showModal === SEND_BRIEF_ALL && <SendBriefAllModal /> }
        { showModal === SEND_DELIVERABLE_ALL && <SendDeliverableAllModal /> }
        <div className="white-header">
          <div className="row large">
            <div className="flex-spacer">
              {
                !campaignLoading &&
                <Link to="/dashboard">
                  <Icon name="angle left" />back
                </Link>
              }
              <h1>
                {campaignName}{' '}
                {isAutomatedCampaign &&
                  _.map(hashtagsArray, ht => (
                    <Label basic color="grey">{ht}</Label>
                  ))
                }
              </h1>
            </div>
            <div className="flex-item">
              { isUserTeamMember &&
                <Menu>
                  <Dropdown
                    text="Settings &nbsp;"
                    icon="settings"
                    options={menuOptions}
                    pointing="right top"
                    item
                  />
                </Menu>
              }
            </div>
          </div>
          { !campaignLoading && isUserTeamMember ?
            <div className="row no-bottom-padding">
              <div className="large-tabs">
                <div
                  className={`flex-item ${activeTab === COLLABORATIONS ? 'active' : ''}`}
                  onClick={this.setActiveTab(COLLABORATIONS)}
                >
                  {
                    isACR
                      ? <Popup trigger={<h4>Setup</h4>} content="Set up your automated channel report" />
                      : <Popup
                        trigger={<h4>Influencers</h4>}
                        content="Manage your campaign influencers. Add, invite and request Authorization."
                      />
                  }
                </div>
                {
                  isAuto &&
                  <div
                    className={`flex-item ${activeTab === NOTIFICATIONS ? 'active' : ''}`}
                    onClick={this.setActiveTab(NOTIFICATIONS)}
                  >
                    <Popup
                      trigger={
                        <h4>
                          <nobr>Notifications <Icon color="pink" name="star outline" /></nobr>
                        </h4>
                      }
                      content="View campaign history and automated email settings"
                    />
                  </div>
                }
                <div
                  className={`flex-item ${activeTab === ANALYTICS ? 'active' : ''}`}
                  onClick={this.setActiveTab(ANALYTICS)}
                >
                  <Popup
                    trigger={<h4>Dashboard</h4>}
                    content="View your campaign performance and share reports"
                  />
                </div>
                {
                  !isACR && <div
                    className={`flex-item ${activeTab === PAYMENTS ? 'active' : ''}`}
                    onClick={this.setActiveTab(PAYMENTS)}
                  >
                    <Popup
                      trigger={<h4><nobr>Influencer fees</nobr></h4>}
                      content="Keep track of your campaign budget and individual influencer fees"
                    />
                  </div>
                }
                {
                  shouldRenderAuditTrail && isManual &&
                  <div
                    className={`flex-item ${activeTab === AUDIT_TRAIL ? 'active' : ''}`}
                    onClick={this.setActiveTab(AUDIT_TRAIL)}
                  >
                    <Popup
                      trigger={<h4>Audit trail</h4>}
                      content="Audit trail description"
                    />
                  </div>
                }
              </div>
            </div>
            :
            <div className="spacer large" />
          }
        </div>
        <div className="page-content">
          {
            activeTab === AUDIT_TRAIL && shouldRenderAuditTrail &&
            <CampaignAuditTrail campaignId={campaignId} csvFileName={campaignName} />
          }
          { (activeTab === COLLABORATIONS || activeTab === GROUP_CHATS) && <div className="grid">
            {!campaignLoading &&
              isUserTeamMember &&
              isACR &&
              <ACRSetup
                campaignId={campaignId}
                acrScanLoading={automationScanLoading}
                addChannelDisabled={!noDiscussions}
                acrScanDisabled={noDiscussions || automationScanLoading}
                channelAdded={!noDiscussions}
                handleACRScan={this.handleACRScan}
              />
            }
            { !campaignLoading &&
              isUserTeamMember &&
              !isACR &&
              <div>
                <div className="budget-col-header">
                  <Segment style={cardStyles}>
                    <Grid>
                      <Grid.Row>
                        {isManual &&
                          <Grid.Column width={3} verticalAlign="middle">
                            <Input
                              icon="search"
                              placeholder="Search collaboration..."
                              onChange={this.onFilterDiscussionListByTermChanged}
                              fluid
                            />
                          </Grid.Column>
                        }
                        <Grid.Column width={isManual ? 3 : 5} verticalAlign="middle" textAlign="center">
                          {isManual &&
                            <DyzioPrimaryButton
                              label="#HASHTAG Scannner"
                              hasIcon
                              fluid
                              labelPosition="right"
                              icon={<Icon name="search" />}
                              onClick={this.handleHashtagScannerState}
                            />
                          }
                          {isAutomatedCampaign &&
                            <div>
                              <strong>Hashtag(s): </strong>
                              {
                                _.map(hashtagsArray, ht => (
                                  <Label
                                    color="pink"
                                    basic
                                    content={ht}
                                  />
                                ))
                              }
                              <Button
                                icon="cogs"
                                size="tiny"
                                onClick={() => this.onSettingsDropdownItemClick('campaign-settings')}
                              />
                            </div>
                          }
                        </Grid.Column>

                        <Grid.Column width={isManual ? 3 : 4} style={{ borderLeft: '1px solid #ddd' }} verticalAlign="middle">
                          {isAutomatedCampaign &&
                            <div>
                              <DyzioPrimaryButton
                                label="Scan latest posts"
                                hasIcon
                                fluid
                                labelPosition="right"
                                icon={<Icon name="search" />}
                                loading={automationScanLoading}
                                disabled={
                                  !campaignJSON.hashtags ||
                                  automationScanLoading ||
                                  noDiscussions
                                }
                                onClick={this.handleAutomatedCampaignScan}
                              />
                            </div>
                          }
                        </Grid.Column>

                        <Grid.Column width={7} textAlign="right" verticalAlign="middle">
                          <CampaignBudget
                            budget={budget}
                            allocated={allocatedBudget}
                            updateBudget={this.updateCampaign}
                            currency={campaignCurrency}
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Segment>
                </div>
              </div>
            }
            { campaignLoading &&
            <div className="grid-6-6 grid-sm-6-6 grid-md-6-6 grid-lg-6-6 grid-xl-6-6">
              <h2>Loading...</h2>
            </div>
            }
            { campaignLoadError &&
            <div className="grid-6-6 grid-sm-6-6 grid-md-6-6 grid-lg-6-6 grid-xl-6-6">
              <div className="hero left">
                <Message color="red">
                  <p>
                    There was a problem loading the campaign: {campaignLoadError}.
                    Please check you are connected to the internet and try again.
                  </p>
                </Message>
              </div>
            </div>
            }
            { campaign.size > 0 &&
            <div>
              <Grid style={{ margin: 14 }}>
                <Grid.Row>
                  <Grid.Column>
                    <StatusBar />
                    {
                      isACR && isUserTeamMember &&
                        <div style={{ margin: 28 }}>
                          <table>
                            <tr>
                              <td>
                                <h2>Tracked Channel</h2>
                                <p style={{ color: 'rgba(0,0,0,.4)' }}>
                                    Instagram data will be automatically tracked from this channel daily
                                </p>
                              </td>
                              <td style={{ paddingLeft: 300 }}>
                                <ApprovalStatusLabel approvalStatus={approvalStatus} />
                              </td>
                            </tr>
                          </table>

                        </div>
                    }
                    <DiscussionsList
                      organisation={organisation}
                      trackedAssetsCount={trackedAssetsCount}
                      currency={campaignCurrency}
                      campaignId={campaignId}
                      onUpdateAllocated={this.onUpdateAllocated}
                      showCollaborationType={activeTab}
                      filterDiscussionListByTerm={filterDiscussionListByTerm}
                      reload={() => {
                        this.props.onLoadingCampaign(campaignId);
                      }}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>

            </div>
            }
          </div>
          }
          { isUserTeamMember && activeTab === ANALYTICS && <Report id={campaignId} /> }
          { isUserTeamMember && activeTab === PAYMENTS && <Payments id={campaignId} />}
          {
            isUserTeamMember && activeTab === NOTIFICATIONS &&
            <CampaignNotificationsTab campaignId={campaignId} campaignJSON={campaignJSON} />
          }
        </div>
        {
          hashtagScannerModalOpen &&
          <HashtagScannerModal
            isEmptyState={!hasInfluencers}
            isScanning={isScanning}
            currentScannedPlatform={currentScannedPlatform}
            onClose={this.handleHashtagScannerState}
            onInputValueChange={this.handleHashtagInputsChange}
            onScanHandler={this.handleScanButtonClick}
            scannerResultsCount={scannerResultsCount}
            hashtags={hashtags}
            hashtagsErrors={hashtagsErrors}
          />
        }
        {
          editCampaignModalOpen && campaign &&
          <AddCampaignModal
            title="Edit campaign details"
            buttonLabel="Save"
            addCampaignFormProps={campaignFormProps}
            onClose={this.toggleEditCampaignModalState}
            onAddCampaignSubmit={this.onSubmitEditCampaignForm}
          />
        }
      </div>
    );
  }
}

const mapStateToProps = state => ({
  campaign: state.campaign.get('campaign'),
  myUserRole: state.campaign.get('myUserRole'),
  members: state.members.get('members'),
  showModal: state.modal.get('showModal'),
  campaignLoadError: state.campaign.get('campaignLoadError'),
  campaignLoading: state.campaign.get('campaignLoading'),
});

export function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    onClearStatusBar: () => dispatch(clearStatusBar()),
    onLoadingCampaign: campaignId => dispatch(loadingCampaign(campaignId)),
    onLoadCampaignAnalytics: data => dispatch(loadCampaignAnalytics(data)),
    onSetModal: modalName => dispatch(setModal(modalName)),
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  hasOrgansation,
  hasRESTClient,
)(Campaign);



/** WEBPACK FOOTER **
 ** ./src/containers/acr-campaign/index.js
 **/